import i18next from "i18next";
import type { ServiceMotiveDTO } from "../../api/servicedesk";
import { NexpimView } from "../../views";
import { makeConfigPage } from "./makeConfigPage";

const text = <T,>(key: keyof T & string) => ({
  kind: "text" as const,
  get: (row: T) => (row[key] as string | null) ?? "",
  set: (row: T, value: string) => ({ ...row, [key]: value }),
  required: true,
});

const check = <T,>(key: keyof T & string) => ({
  kind: "checkbox" as const,
  get: (row: T) => row[key] as boolean,
  set: (row: T, value: boolean) => ({ ...row, [key]: value }),
});

const requireCodeAndName = (row: { code: string | null; name: string | null }): Record<string, string> => {
  const required = i18next.t("validation.required", { defaultValue: "Required" }) as string;
  return {
    ...(row.code?.trim() ? {} : { code: required }),
    ...(row.name?.trim() ? {} : { name: required }),
  };
};

/**
 * Why parts are sent. The service reports are grouped by these, so retire a
 * motive rather than delete it — SIGM did the same by writing "ne plus
 * utiliser" into the name.
 */
export const ServiceMotivePage = makeConfigPage<ServiceMotiveDTO>({
  path: "/servicedesk/service-motive",
  cacheKey: "service-motive",
  titleKey: "navigation.item.config-service-motive",
  titleFallback: "Service motives",
  view: NexpimView.SERVICE_DESK,
  // The service order screen caches the motives under its own key.
  alsoInvalidates: [["servicedesk", "service-motive"]],
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
    // Five of the imported motives are retired; without this they read as live.
    c.text("is_active", (row) => (row.is_active ? t("common.yes", "Yes") : t("common.no", "No")), {
      headerKey: "column.active",
      filter: "select",
      priority: 2,
    }),
  ],
  editor: {
    fields: {
      code: text<ServiceMotiveDTO>("code"),
      name: text<ServiceMotiveDTO>("name"),
      is_active: check<ServiceMotiveDTO>("is_active"),
    },
    newRow: () => ({ id: 0, code: "", name: "", is_active: true }),
    validate: requireCodeAndName,
    unique: ["code"],
    canDelete: false,
  },
});
